import { computed, ref, watch, type Ref } from 'vue';
import type { Router } from 'vue-router';

import { CSP_LIST_KEY, REGION_LIST_KEY } from '@/domains/clusters/queries';
import { invalidateNotifications } from '@/domains/notifications/queries';
import { ACCOUNT_INFO_KEY } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { queryClient } from '@/shared/lib/query-client';
import {
  HOST_EVENTS,
  INVALIDATE_RESOURCE,
  SUB_EVENTS,
  emitMessage,
  onMessage,
  type Bus,
  type HostEventKind,
} from '@celerdata/host-bridge-contract';

import { hostToSubPath } from './legacy-path-alias';

import type { QueryClient } from '@tanstack/vue-query';

// LegacyShellHost flips this on `onMounted` / off on `onBeforeUnmount`.
// Gates all host:* emissions so deltas don't leak into a backgrounded sub
// while the user is on a v3-native route (sub is :alive but not visible).
export const shellActive = ref(false);

// DOM-measured viewport size, fed by AppLayout-scoped resizer (Task 1.2+).
// Lives at module scope so consumers (LegacyShellHost) and the broadcaster
// share the same ref; broadcaster watches it for `host:viewport-changed`
// deltas and reads its current value during `replayAll`.
export const viewportSize = ref<{ height: number } | null>(null);

// Flips true once sub emits SUB_EVENTS.MOUNTED. Lives at module scope —
// NOT inside LegacyShellHost — so the signal survives shell unmount/remount
// cycles. Spec §11 slow-bootstrap case: if the user navigates away from
// the legacy route before sub finishes booting, the shell unmounts; a
// component-scoped listener would be torn down and miss the signal,
// leaving the mask stuck on the next remount. Module scope avoids that.
export const subBundleMounted = ref(false);

interface StateEntry {
  kind: HostEventKind;
  source: Ref<unknown>;
  // `deep` is per-source: only the auth blob nests user/perms/dataPerms
  // sub-objects worth tracking with Vue's deep walker. viewport (`{height}`)
  // and path (`{path}`) are leaf-after-one-hop — deep:true is pure waste.
  deep: boolean;
}

let installedBus: Bus | null = null;
let stateRegistry: ReadonlyArray<StateEntry> | null = null;

// Per-kind signature cache. Set ONLY when an emit actually went out (and
// shellActive was true). Read by emitIfChanged to suppress redundant sends.
// NOT cleared on SUB_EVENTS.MOUNTED — sub's mirror now starts populated via
// the `getInitialState()` boot-time seed (spec §4.0), so a fresh iframe no
// longer needs the host to re-emit every source. `:alive` remount catch-up
// (handleActivated → replayAll) relies on this cache: changes made while
// shellActive was false leave the cache stale (the watcher early-returns
// before lastSig.set), so on remount the current source value differs from
// the cached one and emitIfChanged passes the dedup gate.
const lastSig = new Map<HostEventKind, string>();

function emitIfChanged(bus: Bus, kind: HostEventKind, value: unknown): void {
  // shellActive=false (sub is backgrounded): suppress the emit AND leave the
  // signature stale. Any change made while backgrounded will show up as
  // signature-differs when the shell re-activates and replayAll runs.
  if (!shellActive.value) return;
  // Per-source opt-out via null: a source computed can return null when its
  // value is not applicable to the current context (e.g. subPath only fires
  // for legacy-rendered routes). Clear the sig so the next non-null value
  // is emitted unconditionally — without this, a native-route detour would
  // leave the previous legacy path cached and dedup would drop the return
  // trip when the user browser-backs into the same legacy URL.
  if (value === null) {
    lastSig.delete(kind);
    return;
  }
  const sig = JSON.stringify(value);
  if (lastSig.get(kind) === sig) return;
  lastSig.set(kind, sig);
  // Cast: PayloadOf<K> is narrowed per kind in the contract package, but the
  // registry erases the kind-payload relationship to a single Ref<unknown>.
  // The narrow is recovered at the sub-side onMessage handlers.
  emitMessage(bus, kind, value as never);
}

function getSources(router: Router): ReadonlyArray<StateEntry> {
  const user = useUserStore();
  // accountInfo is intentionally OUT of the auth-changed projection — bridge
  // carries signals, not state (notifications pattern). Cross-user freshness
  // travels via HOST_EVENTS.INVALIDATE / SUB_EVENTS.INVALIDATE, each side
  // owning its own canonical store. Cold-start seed via
  // LegacyShellHost.getInitialState gives sub a starting value.
  const authSnapshot = computed(() => ({
    user: user.info ?? null,
    perms: user.perms,
    dataPerms: user.dataPerms,
  }));
  // Read the router instance directly via `router.currentRoute` rather than
  // `useRoute()` — installBroadcaster is invoked outside any setup() context
  // and useRoute()'s inject contract would break the call site. Returns null
  // when the current route is NOT legacy-rendered so PATH_CHANGED doesn't
  // leak the host's native destination to the sub during a legacy → native
  // handoff (pre-flush watchers fire before LegacyShellHost.beforeUnmount
  // flips shellActive, leaving a same-tick window where the guard alone
  // isn't sufficient).
  const subPath = computed(() => {
    const r = router.currentRoute.value;
    if (r.meta.renderer !== 'legacy') return null;
    return { path: hostToSubPath(r.fullPath) };
  });
  return [
    { kind: HOST_EVENTS.AUTH_CHANGED, source: authSnapshot as Ref<unknown>, deep: true },
    { kind: HOST_EVENTS.VIEWPORT_CHANGED, source: viewportSize as Ref<unknown>, deep: false },
    { kind: HOST_EVENTS.PATH_CHANGED, source: subPath as Ref<unknown>, deep: false },
  ];
}

export function installBroadcaster(bus: Bus, router: Router): void {
  // Idempotency latch — installBroadcaster is fired once at host bootstrap,
  // but HMR / test setup can drive a second call against the same bus and
  // double-register every source watcher, fanning out duplicate emits.
  if (installedBus === bus) return;
  installedBus = bus;
  const registry = getSources(router);
  stateRegistry = registry;

  for (const { kind, source, deep } of registry) {
    watch(source, (value) => emitIfChanged(bus, kind, value), { deep });
  }

  // Module-scope sub:mounted listener — installed once at host bootstrap and
  // intentionally never unsubscribed. installBroadcaster runs at host startup
  // and the listener lives for the host's lifetime; tearing it down on shell
  // unmount would reintroduce the slow-bootstrap mask bug (see export comment).
  //
  // MOUNTED's sole job here is to flip `subBundleMounted` so the host mask's
  // blank-protection layer (§4.0.2) can lift. Replay duty was deleted along
  // with cold-start replay: initial state is now pulled by sub via
  // `getInitialState()` at module-eval (§4.0), so the bus never needs to
  // re-emit on first paint. `:alive` remount catch-up is handled by
  // `:activated` → `replayAll()` in LegacyShellHost (§4.4).
  onMessage(bus, SUB_EVENTS.MOUNTED, () => {
    subBundleMounted.value = true;
  });

  // sub:auth-error funnels into the same handleAuthError as the host's 50001
  // axios branch. installBroadcaster is the natural install point: module-
  // scope, lifetime of the host process, no dependency on broadcaster state
  // — the broadcaster is just the singular host-side bus install site.
  // handleAuthError is lazy-imported to avoid pulling the axios module graph
  // (which transitively reaches element-plus) into broadcaster startup.
  onMessage(bus, SUB_EVENTS.AUTH_ERROR, (payload) => {
    void import('@/shared/lib/http/axios').then(({ handleAuthError }) => {
      void handleAuthError(payload);
    });
  });

  // Sub-owned cluster-context state (cspList, allRegionList) changed inside
  // legacy. Host doesn't mirror the data — it just re-fetches its own
  // canonical query. invalidateQueries is lazy (no-op when no observer).
  // Per-scope routing intentionally skipped: invalidating both keys is cheap
  // and avoids the host having to track which scope changed.
  onMessage(bus, SUB_EVENTS.CONTEXT_INVALIDATED, () => {
    void queryClient.invalidateQueries({ queryKey: CSP_LIST_KEY });
    void queryClient.invalidateQueries({ queryKey: REGION_LIST_KEY });
  });

  // Sub mutated a domain resource (notice.markRead, future cluster ops,
  // …). Invalidate host's own vue-query cache for that resource only —
  // sub already refreshed its own state at the mutation site, so re-
  // emitting host:invalidate here would echo back and cause a redundant
  // Vuex fetch. Adding a new resource = one row in INVALIDATE_HANDLERS.
  onMessage(bus, SUB_EVENTS.INVALIDATE, (payload) => {
    const handler = INVALIDATE_HANDLERS[payload.resource];
    if (!handler) {
      // Mirror the warn-once shape host-bridge-contract uses for unknown
      // envelope versions — silent drop is a debugging black hole when
      // sub starts emitting a new resource and host hasn't shipped the
      // matching handler yet.
      warnUnknownInvalidate(payload.resource);
      return;
    }
    handler(queryClient);
  });
}

const warnedResources = new Set<string>();
function warnUnknownInvalidate(resource: string): void {
  if (warnedResources.has(resource)) return;
  warnedResources.add(resource);
  console.warn(
    `[host-sdk] sub:invalidate received unknown resource "${resource}" — no INVALIDATE_HANDLERS entry. Add one or check the sender.`,
  );
}

// resource → local invalidator. Mirrors sub-side `host-invalidate-plugin.js`
// `RESOURCE_TO_ACTION` in structure — both ends declare what they accept and
// the bus stays a string-keyed lookup, not an exhaustive switch.
const INVALIDATE_HANDLERS: Record<string, (qc: QueryClient) => void> = {
  [INVALIDATE_RESOURCE.NOTIFICATIONS]: invalidateNotifications,
  [INVALIDATE_RESOURCE.ACCOUNT_INFO]: (qc) => {
    void qc.invalidateQueries({ queryKey: ACCOUNT_INFO_KEY });
  },
};

export function replayAll(): void {
  if (!installedBus || !stateRegistry) return;
  if (!shellActive.value) return;
  for (const { kind, source } of stateRegistry) {
    emitIfChanged(installedBus, kind, source.value);
  }
}
